import { Touchpoint, getRuntimeInfo } from '@account/account-portal'
import { Loader } from '@account/account-portal/client'
import {
  ExternalAuthContext,
  ExternalAuthError,
  ExternalAuthErrorCode,
  ExternalAuthErrorType,
  ExternalAuthFlow,
  ExternalAuthPageProps,
  migrateLegacyAuthFlow,
  removeAuthFlow,
  retrieveAuthFlow,
  saveExternalAuthData,
  useExternalAuthData,
} from '@account/account-portal/external-auth'
import {
  Events,
  setExternalAuthError,
  setExternalAuthStart,
  setExternalAuthSuccess,
  updateRuntimeInfo,
} from '@account/account-portal/lib'
import { OriginApp } from '@account/account-portal/types'
import { ConsentRequestPayload } from '@account/consents-shared-lib'
import { instana } from '@crystal/analytics'
import { getQueryParams, MicrofrontendProps } from '@crystal/core'
import { useFeatureFlags } from '@crystal/feature-flags'
import { FC, useEffect } from 'react'

import { useApiClient } from '../api-client'
import { getHandler } from '../handler'
import { HandlerContext } from '../handler/handler-types'
import { isKmcRegistrationFlow } from '../handler/kmc'
import { ExternalAuthPayload } from '../types'

import { AccountPortal } from './account-portal'
import { ExternalAuthErrorModal } from './external-auth-error-modal'

export const ExternalAuthPage: FC<MicrofrontendProps<ExternalAuthPageProps>> = ({ className, context }) => {
  const externalAuthData = useExternalAuthData()
  const { SOCIAL_LOGIN_CONSENTS } = useFeatureFlags()
  const { error, kmc, authContext } = externalAuthData
  const isKmcRegistration = !!kmc && !!authContext?.flow && isKmcRegistrationFlow(authContext.flow)
  const shouldUpdateConsents = !!SOCIAL_LOGIN_CONSENTS && authContext?.shouldUpdateConsents
  const shouldRenderAccountPortal = isKmcRegistration || shouldUpdateConsents
  const handlerContext: HandlerContext = {
    apiClient: useApiClient(),
  }

  useEffect(() => {
    /**
     * TODO:
     * This is a interim solution to support Checkout and Glass social login.
     * Once AccountPortal microfrontend is updated in Checkout and Glass, this can be removed.
     */
    migrateLegacyAuthFlow()
    handleAuthenticationFlow(handlerContext)
  }, [])

  // broadcast the authentication event
  useEffect(() => {
    if (!authContext?.authData || shouldUpdateConsents) {
      return
    }

    const { isAuthProvider, redirectUri, state } = authContext?.flow?.transitData || {}
    if (isAuthProvider) {
      redirectAuthToken(redirectUri as string, state as string)
      return
    }

    if (authContext.appRedirect) {
      // redirecting to external app? save the context for next sessionReload
      saveExternalAuthData(externalAuthData)
      window.location.assign(authContext.appRedirect.path)
      return
    }

    // broadcast external auth success event
    setExternalAuthSuccess(authContext.authData)
  }, [authContext?.authData, shouldUpdateConsents])

  return (
    <>
      {shouldRenderAccountPortal ? (
        <AccountPortal
          context={context}
          preset="kmc-register"
          touchpoint={Touchpoint.RegistrationPage}
          pageContext={getRuntimeInfo().pageContext}
        />
      ) : (
        <Loader />
      )}
      {error && <ExternalAuthErrorModal className={className} />}
    </>
  )
}

async function handleAuthenticationFlow(handlerContext: HandlerContext) {
  const params = getQueryParams()
  const successCode = params.code
  const errorType = params.error as ExternalAuthErrorType | undefined
  const errorCode = params.error_description as ExternalAuthErrorCode | undefined
  let authFlow: ExternalAuthFlow

  try {
    authFlow = retrieveAuthFlow()
  } catch (err) {
    return setExternalAuthError(new ExternalAuthError('Invalid AuthFlow').serialize())
  }

  try {
    const { authContext, pageContext, transitData } = authFlow
    const redirectRoute = authContext.redirectRoute || pageContext.originRoute
    const appRedirect = authContext.appRedirect || pageContext.originApp || (pageContext.originRoute as OriginApp)
    const handler = getHandler(authFlow, handlerContext)

    updateRuntimeInfo({
      pageContext,
      isAuthProvider: transitData?.isAuthProvider as boolean,
    })
    setExternalAuthStart<ExternalAuthContext>({
      flow: authFlow,
      appRedirect, // appRedirect to hard-redirect using browser reload
      redirectRoute, // redirectRoute to soft-redirect using React Router(mostly applicable to Glass)
    })
    instana('reportEvent', Events.ExternalAuthProcess, {
      meta: {
        method: authFlow.method,
        provider: authFlow.provider,
      },
    })

    // having error params means to handle the response
    if (errorType) {
      instana('reportEvent', Events.ExternalAuthError, {
        meta: {
          errorType,
          errorCode: errorCode || '',
        },
      })
      await handler.handleError(
        new ExternalAuthError('Auth error', {
          serverErrorType: errorType,
          serverErrorCode: errorCode,
          visible: true,
        }),
      )
      return
    }
    // having code params means to handle the response
    if (successCode) {
      const payload: ExternalAuthPayload = {
        code: successCode,
        persistentLogin: authContext.persistentLogin,
        sourceId: authContext.sourceId,
      }
      if (authContext.storeId) {
        payload.storeId = authContext.storeId
      }
      if (transitData?.legalAcceptance) {
        payload.legalAcceptance = transitData?.legalAcceptance as ConsentRequestPayload['legalAcceptance']
      }
      await handler.handleAuth(payload)
      instana('reportEvent', Events.ExternalAuthSuccess)
      return
    }

    // this is unexpected
    throw new ExternalAuthError('Invalid redirect params', {
      details: { params },
    })
  } catch (err) {
    instana('reportError', err as Error)
    handleGlobalError(err)
  } finally {
    removeAuthFlow()
  }
}

function handleGlobalError(err: unknown) {
  if (err instanceof ExternalAuthError) {
    return setExternalAuthError(err.serialize())
  }

  if (err instanceof Error) {
    return setExternalAuthError(
      new ExternalAuthError(err.message, {
        details: { ...err },
      }).serialize(),
    )
  }

  setExternalAuthError(new ExternalAuthError(err as string).serialize())
}

function redirectAuthToken(redirectUri: string, state?: string) {
  const params = [['redirect_uri', redirectUri]]
  if (state) {
    params.push(['state', state])
  }
  const authParams = params.map(([k, v]) => `${k}=${v}`).join('&')
  location.assign(`${location.origin}/api/account/auth-redirect?${authParams}`)
}
